// File: src/pages/AINetworkingAssistantPage.jsx
// Description: AI-powered networking assistant providing personalized advice and strategies

import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Paper,
  Avatar,
  Chip,
  useTheme,
  useMediaQuery,
  Stack,
  Divider,
  IconButton,
  Tooltip,
  Alert,
  LinearProgress,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Collapse,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  SmartToy,
  Send,
  Lightbulb,
  Psychology,
  TrendingUp,
  Chat,
  ExpandMore,
  Work,
  School,
  Business,
  Person,
  AutoAwesome,
  KeyboardArrowDown
} from '@mui/icons-material';
import { Mic, MicOff } from '@mui/icons-material';
import { useAI } from '../context/AIContext';
import { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import AIService from '../services/AIService';
import { createAuthenticatedAPI } from '../utils/apiClient';

const AINetworkingAssistantPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const {
    chatHistory,
    aiInsights,
    networkingTips,
    addChatMessage,
    addAIInsight,
    addNetworkingTip
  } = useAI();

  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('chat');
  const [collaborationData, setCollaborationData] = useState({
    targetName: '',
    targetCompany: '',
    targetRole: '',
    context: ''
  });
  const [generatedPitch, setGeneratedPitch] = useState(null);
  const chatEndRef = useRef(null);
  const recognitionRef = useRef(null);
  const [sttAvailable, setSttAvailable] = useState(false);
  const [listening, setListening] = useState(false);

  // Create authenticated API client
  const api = createAuthenticatedAPI({ user, logout, navigate });

  useEffect(() => {
    if (chatEndRef.current) {
      setTimeout(() => {
        chatEndRef.current?.scrollIntoView({ 
          behavior: 'smooth',
          block: 'end',
          inline: 'nearest'
        });
      }, 100);
    }
  }, [chatHistory, loading]);

  const scrollToBottom = () => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ 
        behavior: 'smooth',
        block: 'end',
        inline: 'nearest'
      });
    }
  };

  // Setup browser SpeechRecognition availability
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    setSttAvailable(!!SpeechRecognition);
  }, []);

  const startRecognition = () => {
    if (listening) return;
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return;
    try {
      const rec = new SpeechRecognition();
      recognitionRef.current = rec;
      rec.lang = navigator.language || 'en-US';
      rec.interimResults = true;
      rec.continuous = false;
      let finalText = '';
      rec.onresult = (event) => {
        let interim = '';
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalText += transcript + ' ';
          } else {
            interim += transcript;
          }
        }
        const combined = (inputMessage ? inputMessage + ' ' : '') + (finalText + interim).trim();
        setInputMessage(combined);
      };
      rec.onerror = () => {
        setListening(false);
      };
      rec.onend = () => {
        setListening(false);
      };
      rec.start();
      setListening(true);
    } catch (e) {
      setListening(false);
    }
  };

  const stopRecognition = () => {
    try {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    } catch {}
    setListening(false);
  };

  useEffect(() => {
    if (aiInsights.length === 0) {
      const mockInsight = AIService.generateMockInsight();
      addAIInsight({ text: mockInsight, type: 'networking' });
    }
  }, [aiInsights.length, addAIInsight]);

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return;

    const userMessage = inputMessage.trim();
    setInputMessage('');
    addChatMessage(userMessage, false);

    setLoading(true);
    try {
      const userProfile = {
        name: user?.name || 'You',
        jobTitle: user?.jobTitle || 'Professional',
        industry: user?.industry || 'General',
        experience: user?.experience || 'Various',
        location: user?.location || 'Global',
        skills: user?.skills || ['Networking', 'Communication'],
        bio: user?.bio || 'Professional seeking career advice'
      };

      const advice = await AIService.getGeneralAssistance(userProfile, userMessage);
      let aiResponse = advice || "I'm here to help with your professional development questions! Please ask me anything about career growth, networking, skills development, or professional challenges.";

      addChatMessage(aiResponse, true);
      addAIInsight({ text: aiResponse, type: 'networking' });
    } catch (error) {
      console.error('Error getting AI advice:', error);
      addChatMessage("I'm sorry, I encountered an error connecting to the AI server. Please try again or check your internet connection.", true);
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePitch = async () => {
    if (!collaborationData.targetName || !collaborationData.context) {
      return;
    }

    setLoading(true);
    try {
      const userProfileForPitch = { 
        name: user?.name || 'You',
        jobTitle: user?.jobTitle || 'Professional',
        industry: user?.industry || 'General',
        skills: user?.skills || ['Networking', 'Communication'],
        bio: user?.bio || 'Professional seeking collaboration opportunities'
      };

      const pitch = await AIService.generateCollaborationPitch(
        userProfileForPitch,
        { 
          name: collaborationData.targetName,
          company: collaborationData.targetCompany,
          role: collaborationData.targetRole,
          industry: 'your field'
        }
      );
      const lines = pitch.split('\n');
      let subject = 'Potential Collaboration Opportunity';
      let pitchContent = pitch;
      let followUp = 'Send a personalized follow-up within 48 hours';
      if (pitch.includes('SUBJECT:')) {
        const subjectMatch = pitch.match(/SUBJECT:\s*(.+)/);
        if (subjectMatch) subject = subjectMatch[1];
      }
      if (pitch.includes('FOLLOW-UP:')) {
        const followUpMatch = pitch.match(/📅 FOLLOW-UP:\s*(.+)/);
        if (followUpMatch) followUp = followUpMatch[1];
      }
      setGeneratedPitch({ subject, pitch: pitchContent, followUp });
    } catch (error) {
      console.error('Failed to generate pitch:', error);
      setGeneratedPitch({
        subject: 'Potential Collaboration Opportunity',
        pitch: `Hi ${collaborationData.targetName},\n\nI came across your profile and was impressed by your work at ${collaborationData.targetCompany}. ${collaborationData.context}\n\nI believe we could create something valuable together. Would you be interested in a brief conversation to explore potential collaboration opportunities?\n\nBest regards,\n${user?.name || 'You'}`,
        followUp: 'Send a personalized follow-up within 48 hours'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const formatAIResponse = (text) => {
    if (!text) return text;
    let formatted = text
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n')
      .replace(/(🎯|📋|💡|📅|⚡|✅)/g, '\n\n$1 ')
      .replace(/(•)/g, '\n  $1 ')
      .replace(/\n\s*\n\s*\n/g, '\n\n')
      .replace(/\n\s*\n/g, '\n\n')
      .trim();
    return formatted;
  };

  const processTextForDisplay = (text) => {
    if (!text) return text;
    const sections = text.split(/(?=🎯|📋|💡|📅|⚡|✅)/);
    return sections.map((section) => {
      if (section.trim()) {
        const cleaned = section
          .replace(/^\s+/, '')
          .replace(/\s+$/, '')
          .replace(/\n{3,}/g, '\n\n')
          .trim();
        return cleaned;
      }
      return '';
    }).filter(Boolean).join('\n\n');
  };

  const handleLongWords = (text) => {
    if (!text) return text;
    return text.split(' ').map(word => {
      if (word.length > 20) {
        return `<span class="long-word">${word}</span>`;
      }
      return word;
    }).join(' ');
  };

  const getProcessedText = (text) => {
    if (!text) return text;
    let processed = processTextForDisplay(text);
    processed = handleLongWords(processed);
    return processed;
  };

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .chat-message-text { line-height: 1.7 !important; font-size: 0.9rem !important; }
      .chat-container { word-break: normal !important; overflow-wrap: break-word !important; hyphens: manual !important; }
      .long-word { word-break: break-all; }
    `;
    document.head.appendChild(style);
    return () => { document.head.removeChild(style); };
  }, []);

  const quickQuestions = [
    "How can I advance my career in my current field?",
    "What skills should I develop next?",
    "How do I build a strong professional network?",
    "What's the best way to handle workplace challenges?",
    "How can I improve my personal branding?"
  ];

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: { xs: 2.5, md: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: { xs: 1, md: 2 }, mb: { xs: 1.25, md: 1.75 }, flexDirection: { xs: 'column', sm: 'row' } }}>
          <SmartToy sx={{ fontSize: { xs: 32, md: 40 }, color: theme.palette.secondary.main }} />
          <Typography variant={isMobile ? "h4" : "h3"} sx={{ fontWeight: 700, color: theme.palette.text.primary, textAlign: { xs: 'center', md: 'left' } }}>
            AI Assistant
          </Typography>
        </Box>
        <Typography variant={isMobile ? "body1" : "h6"} color="text.secondary" sx={{ maxWidth: '600px', mx: 'auto', px: { xs: 2, md: 0 }, lineHeight: 1.6 }}>
          Get instant AI-powered assistance for career development, networking, and professional growth.
        </Typography>
      </Box>

      {/* Quick Start Guide */}
      <Card sx={{ mb: { xs: 2.5, md: 3 }, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', mx: { xs: 1, md: 0 } }}>
        <CardContent sx={{ p: { xs: 1.75, md: 2.5 } }}>
          <Typography variant={isMobile ? "body1" : "h6"} gutterBottom fontWeight="bold" sx={{ mb: 1.5, textAlign: 'center' }}>
            🚀 How to Use Your AI Assistant
          </Typography>
          <Grid container spacing={{ xs: 1, md: 2 }}>
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <Chat sx={{ fontSize: { xs: 24, md: 32 }, mb: 1 }} />
                <Typography variant={isMobile ? "body2" : "body1"} sx={{ fontWeight: 500, mb: 0.5 }}>
                  Ask Questions
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.8, display: 'block' }}>
                   Get personalized career advice
                 </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <Work sx={{ fontSize: { xs: 24, md: 32 }, mb: 1 }} />
                <Typography variant={isMobile ? "body2" : "body1"} sx={{ fontWeight: 500, mb: 0.5 }}>
                  Generate Pitches
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.8, display: 'block' }}>
                  Create collaboration proposals
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <Lightbulb sx={{ fontSize: { xs: 24, md: 32 }, mb: 1 }} />
                <Typography variant={isMobile ? "body2" : "body1"} sx={{ fontWeight: 500, mb: 0.5 }}>
                  Get Insights
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.8, display: 'block' }}>
                   Get professional insights
                 </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Box sx={{ mb: { xs: 2.5, md: 3 }, px: { xs: 1, md: 0 } }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1, sm: 1 }} justifyContent="center" sx={{ '& .MuiButton-root': { minWidth: { xs: '100%', sm: '140px' }, height: { xs: '44px', sm: '38px' }, fontSize: { xs: '0.875rem', sm: '0.9rem' } } }}>
          {[{ key: 'chat', label: 'Chat', icon: <Chat /> }, { key: 'insights', label: 'Insights', icon: <Lightbulb /> }, { key: 'collaboration', label: 'Pitch Generator', icon: <Work /> }, { key: 'tips', label: 'Resources & Tips', icon: <TrendingUp /> }].map((tab) => (
            <Button key={tab.key} variant={activeTab === tab.key ? 'contained' : 'outlined'} startIcon={tab.icon} onClick={() => setActiveTab(tab.key)} sx={{ background: activeTab === tab.key ? 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)' : 'transparent', borderRadius: { xs: 2, sm: 1 }, boxShadow: activeTab === tab.key ? '0 4px 12px rgba(30, 64, 175, 0.3)' : 'none', '&:hover': { background: activeTab === tab.key ? 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%)' : 'rgba(30, 64, 175, 0.08)', transform: activeTab === tab.key ? 'translateY(-1px)' : 'none' }, transition: 'all 0.2s ease-in-out' }}>
              {tab.label}
            </Button>
          ))}
        </Stack>
      </Box>

      {/* Content Area */}
      <Grid container spacing={{ xs: 2, md: 3 }}>
        <Grid item xs={12} lg={8}>
          {activeTab === 'chat' && (
            <Card sx={{ minHeight: { xs: '520px', md: '640px' }, display: 'flex', flexDirection: 'column', mx: { xs: 1, md: 0 } }}>
              <CardContent sx={{ p: { xs: 1.5, md: 2 }, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography variant={isMobile ? "h6" : "h5"} sx={{ fontWeight: 600, mb: { xs: 1.25, md: 1.75 }, textAlign: { xs: 'center', md: 'left' } }}>
                  Ask Your AI Assistant
                </Typography>
                <Box className="chat-container" sx={{ flexGrow: 1, overflowY: 'auto', mb: { xs: 1.25, md: 1.5 }, p: { xs: 1.5, md: 2 }, bgcolor: theme.palette.background.default, borderRadius: 2, border: `1px solid ${theme.palette.divider}`, minHeight: { xs: '260px', md: '360px' }, maxHeight: { xs: '360px', md: '460px' }, position: 'relative', '&::-webkit-scrollbar': { width: { xs: '6px', md: '8px' } }, '&::-webkit-scrollbar-track': { backgroundColor: theme.palette.grey[100], borderRadius: { xs: '3px', md: '4px' } }, '&::-webkit-scrollbar-thumb': { backgroundColor: theme.palette.grey[400], borderRadius: { xs: '3px', md: '4px' }, '&:hover': { backgroundColor: theme.palette.grey[500] } }, '& *': { wordBreak: 'normal', overflowWrap: 'break-word', hyphens: 'manual' } }}>
                  <Box sx={{ position: 'sticky', top: 0, right: 0, zIndex: 10, display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                    <IconButton onClick={scrollToBottom} sx={{ bgcolor: theme.palette.primary.main, color: 'white', '&:hover': { bgcolor: theme.palette.primary.dark }, boxShadow: '0 2px 8px rgba(0,0,0,0.15)', width: { xs: 32, md: 36 }, height: { xs: 32, md: 36 }, '&:active': { transform: 'scale(0.95)' } }} size="small">
                      <KeyboardArrowDown sx={{ fontSize: { xs: 16, md: 20 } }} />
                    </IconButton>
                  </Box>
                  {chatHistory.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 3 }}>
                      <SmartToy sx={{ fontSize: 40, color: theme.palette.text.secondary, mb: 1.5 }} />
                      <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                        Start a conversation with your AI assistant
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Ask about career development, professional challenges, or use the quick questions below
                      </Typography>
                    </Box>
                  ) : (
                    <Stack spacing={{ xs: 2, md: 3 }} sx={{ width: '100%', pb: 2 }}>
                      {chatHistory.map((message) => (
                        <Box key={message.id} sx={{ display: 'flex', justifyContent: message.isAI ? 'flex-start' : 'flex-end', width: '100%', mb: { xs: 1, md: 2 }, position: 'relative', zIndex: 1 }}>
                          <Box sx={{ maxWidth: { xs: '90%', md: '85%' }, minWidth: { xs: '200px', md: '280px' }, bgcolor: message.isAI ? theme.palette.primary[50] : theme.palette.secondary[50], border: `1px solid ${message.isAI ? theme.palette.primary[200] : theme.palette.secondary[200]}`, borderRadius: { xs: 2, md: 3 }, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', p: { xs: 2, md: 3 }, position: 'relative', overflow: 'visible', minHeight: 'fit-content', wordWrap: 'break-word' }}>
                            <Typography className="chat-message-text" variant={isMobile ? "body2" : "body1"} sx={{ lineHeight: 1.7, whiteSpace: 'pre-wrap', fontSize: { xs: '0.8rem', md: '0.9rem' }, wordBreak: 'normal', overflowWrap: 'break-word', hyphens: 'manual', textAlign: 'left', mb: { xs: 1.5, md: 2 }, fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif', overflow: 'visible', textOverflow: 'clip', textRendering: 'optimizeLegibility', WebkitFontSmoothing: 'antialiased', MozOsxFontSmoothing: 'grayscale', '& .emoji-section': { fontWeight: 700, color: theme.palette.primary.main, display: 'block', marginTop: { xs: '8px', md: '12px' }, marginBottom: { xs: '6px', md: '8px' }, fontSize: { xs: '0.9rem', md: '1rem' } }, '& strong': { fontWeight: 600, color: theme.palette.text.primary }, '& em': { fontStyle: 'italic', color: theme.palette.text.secondary }, '& .long-word': { wordBreak: 'break-all', overflowWrap: 'break-word' } }} component="div">
                              {message.isAI ? getProcessedText(message.text) : message.text}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', position: 'absolute', bottom: '-20px', left: 0, fontSize: { xs: '0.65rem', md: '0.75rem' }, fontWeight: 500 }}>
                              {new Date(message.timestamp).toLocaleTimeString()}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                      {loading && (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                          <Paper sx={{ p: { xs: 2, md: 3 }, bgcolor: theme.palette.primary[50], borderRadius: { xs: 2, md: 3 }, border: `1px solid ${theme.palette.primary[200]}` }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, md: 2 } }}>
                              <CircularProgress size={isMobile ? 16 : 20} />
                              <Typography variant={isMobile ? "body2" : "body1"} color="text.secondary" sx={{ fontWeight: 500 }}>
                                AI is thinking...
                              </Typography>
                            </Box>
                          </Paper>
                        </Box>
                      )}
                    </Stack>
                  )}
                  <div ref={chatEndRef} />
                </Box>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1.25 }}>
                  <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                    <TextField fullWidth multiline minRows={2} placeholder={sttAvailable ? (listening ? 'Listening… speak now' : 'Type or use the mic…') : 'Type your message…'} value={inputMessage} onChange={(e) => setInputMessage(e.target.value)} onKeyDown={handleKeyPress} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: '0.95rem' } }} />
                    <Tooltip title={sttAvailable ? (listening ? 'Stop listening' : 'Start voice input') : 'Voice input not supported on this browser'}>
                      <span>
                        <IconButton color={listening ? 'error' : 'primary'} onClick={listening ? stopRecognition : startRecognition} disabled={!sttAvailable} sx={{ alignSelf: 'center' }}>
                          {listening ? <MicOff /> : <Mic />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                  <Button variant="contained" endIcon={<Send />} onClick={handleSendMessage} disabled={loading} sx={{ alignSelf: { xs: 'stretch', sm: 'center' }, py: 1 }}>
                    Send
                  </Button>
                </Stack>
                {sttAvailable ? (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                    Tip: tap the mic to dictate; tap again to stop.
                  </Typography>
                ) : (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                    Voice input isn’t supported in this browser.
                  </Typography>
                )}
              </CardContent>
            </Card>
          )}
        </Grid>
        <Grid item xs={12} lg={4}>
          <Card sx={{ mb: 2 }}>
            <CardContent sx={{ p: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Lightbulb sx={{ color: theme.palette.warning.main, mr: 1 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Quick Questions
                </Typography>
              </Box>
              <Stack spacing={1}>
                {quickQuestions.slice(0, 3).map((q, idx) => (
                  <Button key={idx} variant="outlined" size="small" onClick={() => setInputMessage(q)} sx={{ justifyContent: 'flex-start', textAlign: 'left' }}>
                    {q}
                  </Button>
                ))}
              </Stack>
            </CardContent>
          </Card>
          <Card>
            <CardContent sx={{ p: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <AutoAwesome sx={{ color: theme.palette.secondary.main, mr: 1 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Tips & Insights
                </Typography>
              </Box>
              <List dense sx={{ py: 0 }}>
                {aiInsights.slice(0, 5).map((insight, index) => (
                  <ListItem key={index} sx={{ py: 0.5 }}>
                    <ListItemIcon>
                      <AutoAwesome sx={{ fontSize: 18, color: theme.palette.text.secondary }} />
                    </ListItemIcon>
                    <ListItemText primaryTypographyProps={{ variant: 'body2' }} primary={insight.text} />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
}

export default AINetworkingAssistantPage;
